import cx from 'classnames'
import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'

import { paperStyles } from '../../libs/paperStyles'
import { zIndexStyles } from '../../libs/zIndexStyles'

type Props = {
  children: ReactNode
  isContrast?: boolean
  /**
   * Fraction of how far the animation should be.
   * 1 means all the way from the action icon to the children size. 0 means no animation.
   * Default is 0.5
   */
  animationFraction?: 1 | 0.75 | 0.5 | 0.25 | 0
  trigger: ReactNode
} & (
  | {
      variant: 'horizontal'
      grow: 'left' | 'right'
    }
  | {
      variant: 'vertical'
      grow?: never
    }
)

export const ActionDisclosure = ({
  children,
  trigger,
  animationFraction = 0.5,
  variant,
  ...props
}: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [triggerSize, setTriggerSize] = useState<number>(0)
  const [childrenSize, setChildrenSize] = useState<number>(0)
  const childrenRef = useRef<HTMLDivElement>(null)

  const handleTriggerMounted = (ref: HTMLDivElement) => {
    const refSize =
      variant === 'horizontal' ? ref?.clientWidth : ref?.clientHeight

    if (refSize && refSize !== triggerSize) {
      setTriggerSize(refSize)
    }
  }

  useEffect(() => {
    if (childrenRef.current) {
      const refSize =
        variant === 'horizontal'
          ? childrenRef.current.clientWidth
          : childrenRef.current.clientHeight
      setChildrenSize(refSize)
    }
    // We deliberately only do this on mount to recomputing when animating
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      tabIndex={0}
      onMouseEnter={() => setIsOpen(true)}
      onFocus={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      onBlur={() => setIsOpen(false)}
      className="group relative w-min focus:outline-0 focus:ring-0 focus:ring-offset-0"
      style={
        {
          '--trigger-size': `${triggerSize}px`,
          '--start-size': `${triggerSize + (childrenSize - triggerSize) * (1 - animationFraction)}px`,
          '--end-size': `${childrenSize}px`,
        } as CSSProperties
      }
      onClick={(event) => event.stopPropagation()}
    >
      <div
        ref={childrenRef}
        className={cx(
          'absolute flex shadow-sm items-center rounded-xl group-focus:ring-1 top-1/2 -translate-y-1/2 p-1 invisible',
          zIndexStyles('popovers'),
          paperStyles({ ringColor: 'grey' }),
          {
            vertical: cx(
              'flex-col',
              isOpen && 'animate-[grow-vertical_100ms_ease-in-out_forwards]',
            ),
            horizontal: cx(
              isOpen && 'animate-[grow-horizontal_100ms_ease-in-out_forwards]',
              props.grow === 'left' &&
                '-translate-x-[calc(100%-var(--trigger-size))]',
            ),
          }[variant],
        )}
      >
        {children}
      </div>
      <div className="w-min" ref={handleTriggerMounted}>
        {trigger}
      </div>
    </div>
  )
}
